import { createSlice } from "@reduxjs/toolkit";

import { removeById } from "../tracked/trackedSlice";

export const INITIAL_SORTING = { field: "createdDate", direction: "DESC" };
export const DEFAULT_FILTERS = { keywords: "" };
export const DEFAULT_ADDITIONAL_FILTERS = { active: true };

export const INITIAL_STATE = {
  data: [],
  currentFilter: { ...DEFAULT_FILTERS },
  savedSearches: [],
  selectedSearchId: null,
  isLoading: false,
  isLoadingMore: false,
  isLastPage: false,
  isInitialized: false,
  pageNumber: 1,
  savedFilters: [],
  proposalStarted: false,
  totalResults: 0,
  currentResults: 0,
  selectedRowIndex: -1,
  sorting: INITIAL_SORTING,
  showEditModal: false,
  editModalFocusField: null,
  appliedFilter: {},
  additionalFilters: { ...DEFAULT_ADDITIONAL_FILTERS },
  isCustomLastUpdated: false,
  lastUpdatedValue: 0,
  tabConfiguration: undefined,
  preserveFiltersForType: null,
};

const opportunitiesSlice = createSlice({
  name: "opportunities",
  initialState: INITIAL_STATE,
  reducers: {
    reset() {
      return INITIAL_STATE;
    },
    setData(state, { payload }) {
      const { data = [], isLastPage, paginate, pageNumber } = payload || {};
      state.pageNumber = pageNumber || 1;
      state.isLastPage = isLastPage;

      if (paginate) {
        state.data = (state.data || []).concat(data);
      } else {
        state.data = data;
      }
    },
    setSorting(state, { payload }) {
      state.sorting = payload || INITIAL_SORTING;
    },
    setCurrentFilter(state, { payload }) {
      state.currentFilter = payload || {};
    },
    clearCurrentFilter(state) {
      for (const key in state.currentFilter) {
        state.currentFilter[key] = "";
      }
    },
    resetCurrentFilter(state) {
      state.currentFilter = { ...DEFAULT_FILTERS };
    },
    setSavedSearches(state, action) {
      state.savedSearches = action.payload;
    },
    setSelectedSearchId(state, { payload }) {
      state.selectedSearchId = payload || null;
    },
    setTabConfiguration(state, { payload }) {
      state.tabConfiguration = payload || null;
    },
    trackOpportunityByExternalId(state, { payload }) {
      state.data = state.data.map((opportunity) => {
        if (opportunity.externalId === payload.externalId) {
          return {
            ...opportunity,
            tracked: true,
            opportunityId: payload.opportunityId,
          };
        }
        return opportunity;
      });
    },
    trackOpportunityByOppId(state, { payload }) {
      state.data = state.data.map((opportunity) => {
        if (opportunity.opportunityId === payload.opportunityId) {
          return {
            ...opportunity,
            tracked: true,
          };
        }
        return opportunity;
      });
    },
    trackEBuyOpportunityByRequestId(state, { payload }) {
      state.data = state.data.map((opportunity) => {
        if (opportunity.requestId === payload.requestId) {
          return {
            ...opportunity,
            tracked: true,
            opportunityId: payload.opportunityId,
          };
        }
        return opportunity;
      });
    },
    trackFindATenderOpportunityByOcid(state, { payload }) {
      state.data = state.data.map((opportunity) => {
        if (opportunity.ocid === payload.ocid) {
          return {
            ...opportunity,
            tracked: true,
            opportunityId: payload.opportunityId,
          };
        }
        return opportunity;
      });
    },
    setIsLoading(state, { payload: isLoading = false }) {
      state.isLoading = isLoading;
    },
    setIsLoadingMore(state, { payload: isLoadingMore = false }) {
      state.isLoadingMore = isLoadingMore;
    },
    setIsInitialized(state, { payload }) {
      state.isInitialized = Boolean(payload);
    },
    setTotalResults(state, { payload }) {
      state.totalResults = payload || 0;
    },
    setCurrentResults(state, { payload }) {
      state.currentResults = payload || 0;
    },
    setSelectedRowIndex(state, { payload }) {
      state.selectedRowIndex = payload || -1;
    },
    setShowEditModal(state, { payload }) {
      state.showEditModal = payload;

      if (!payload) {
        state.editModalFocusField = null;
      }
    },
    setEditModalFocusField(state, { payload }) {
      state.editModalFocusField = payload || null;
    },
    setAppliedFilter(state, action) {
      state.appliedFilter = action.payload || {};
    },
    setAdditionalFilters(state, { payload }) {
      state.additionalFilters = payload || {};
    },
    setIsCustomLastUpdated(state, { payload }) {
      state.isCustomLastUpdated = payload;
    },
    setLastUpdatedValue(state, { payload }) {
      state.lastUpdatedValue = payload || 0;
    },
    setPreserveFiltersForType(state, { payload }) {
      state.preserveFiltersForType = payload ?? null;
    },
  },
  extraReducers: (builder) => {
    // If a user untracks an opportunity, unset the field
    // from memory if it's already there.
    builder.addCase(removeById, (state, { payload }) => {
      state.data = state.data.map((opp) => {
        if (opp.opportunityId !== payload) {
          return opp;
        }
        return { ...opp, tracked: false };
      });
    });
  },
});

export const {
  reset,
  setData,
  setCurrentFilter,
  resetCurrentFilter,
  clearCurrentFilter,
  setSavedSearches,
  setSelectedSearchId,
  trackOpportunityByExternalId,
  trackOpportunityByOppId,
  trackEBuyOpportunityByRequestId,
  trackFindATenderOpportunityByOcid,
  setIsLoading,
  setIsLoadingMore,
  setIsInitialized,
  setTotalResults,
  setCurrentResults,
  setSorting,
  setSelectedRowIndex,
  setShowEditModal,
  setEditModalFocusField,
  setAppliedFilter,
  setAdditionalFilters,
  setIsCustomLastUpdated,
  setLastUpdatedValue,
  setTabConfiguration,
  setPreserveFiltersForType,
} = opportunitiesSlice.actions;

export default opportunitiesSlice;
